<template>
  <modal name="bookACallModal" height="auto" width="850" :adaptive="true">
    <div class="container-close">
      <div class="close-button" @click="closeModal">
        <span class="material-icons">close</span>
      </div>
    </div>
    <div class="modal-content">
      <div class="container welcome-elements">
        <p>Welcome to</p>
        <SwoopLogo class="swoop-logo" height="64px" />
      </div>

      <div class="container">
        <p class="title">Connect Sage Intacct to view your business health report</p>
        <p class="subtitle">By completing your Sage Intacct setup, you’ll unlock a detailed business health report, which include credit scores and other financial insights.</p>
      </div>

      <div class="container funding-options">
        <div>
          <span class="material-icons-outlined">analytics</span>
          <p>Detailed business<br />health reports</p>
        </div>
      </div>

      <button class="btn btn-primary book-call-button" @click="handleBookSetupCall">
        Book my setup session
        <span class="material-icons-outlined icon">contact_phone</span>
      </button>

      <button class="btn btn-link text-link" @click="handleScheduledSetup">I've scheduled my setup</button>
    </div>
  </modal>
</template>

<script>
import SwoopLogo from '@/components/SwoopLogo.vue'
import useWhitelabel from '@/composables/useWhitelabel'
import useSegmentAnalytics from '@/composables/segmentTracking'
import { mapGetters, mapActions } from 'vuex'
import { apiDeckGetters } from '@/store/modules/apiDeck/routines'
import { authGetters } from '@/store/modules/auth/routines'
import { bookACallModalGetters, bookACallModalRoutine } from '@/store/modules/displayModals/routines'
// TODO: revert to these types once the integration refactor work is complete
// import { STATUS } from '@/utils/integrationService'

export default {
  name: 'BookACall',
  components: {
    SwoopLogo
  },
  setup() {
    const { config: globalConfig } = useWhitelabel('global')
    const { trackEvent } = useSegmentAnalytics()

    return { globalConfig, trackEvent }
  },
  computed: {
    ...mapGetters({
      apiDeckIntegration: apiDeckGetters.APIDECK_INTEGRATION,
      user: authGetters.USER,
      modalSeen: bookACallModalGetters.BOOK_A_CALL_MODAL_SEEN
    }),
    isNotOnAuthOrOnboardingPage() {
      const excludedRoutes = ['login', 'sign-up', 'forgot-password', 'stage', 'get-started', 'sage-onboarding', 'make-a-change', 'details-page']

      return !excludedRoutes.includes(this.$route.name)
    },
    isApiDeckLoaded() {
      return this.apiDeckIntegration !== undefined && this.apiDeckIntegration !== null
    },
    hasIntegration() {
      return this.apiDeckIntegration && this.apiDeckIntegration.status === 'connected'
      // TODO: Revert to the following line once the integration refactor work is complete
      // return this.apiDeckIntegration && this.apiDeckIntegration.status === STATUS.CONNECTED
    },
    hasRequiredUtmParameters() {
      const urlParams = new URLSearchParams(window.location.search)
      const urlHasParams = urlParams.get('utm_source') === 'sage'

      if (this.user?.utm) {
        const userUtm = this.user.utm
        const userHasParams = userUtm.source === 'sage'

        return urlHasParams || userHasParams
      }

      return urlHasParams
    },
    isNA() {
      return ['US', 'CA'].includes(this.globalConfig.countryCode)
    },
    shouldShowModal() {
      return this.isNA && this.hasRequiredUtmParameters && !this.hasIntegration && this.isNotOnAuthOrOnboardingPage && this.isApiDeckLoaded && !this.modalSeen
    }
  },
  mounted() {
    setTimeout(() => {
      if (this.shouldShowModal) {
        this.showBookACallModal()
      }
    }, 500)
  },
  watch: {
    shouldShowModal(newVal) {
      if (newVal) {
        this.showBookACallModal()
      }
    }
  },
  methods: {
    ...mapActions({
      setModalSeen: bookACallModalRoutine.TRIGGER
    }),
    closeModal() {
      this.$modal.hide('bookACallModal')
    },
    handleBookSetupCall() {
      this.trackEvent('Button Clicked', { buttonText: 'Book Setup Call' })
      window.open('https://meetings-eu1.hubspot.com/david-twomey/integration-walkthrough', '_blank', 'noopener')
      this.closeModal()
    },
    handleScheduledSetup() {
      this.trackEvent('Button Clicked', { buttonText: "I've scheduled my setup" })
      this.closeModal()
    },
    showBookACallModal() {
      this.$modal.show('bookACallModal')
      this.setModalSeen(true)
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/styles/swoop/_variables';

.v--modal-overlay[data-modal='bookACallModal'] {
  background-color: rgba(0, 62, 82, 0.96);

  .v--modal {
    padding: 0;
    border-radius: 16px;
  }
}

.container-close {
  position: relative;
  padding: 16px;
  top: 0;
  left: 0;
  width: 100%;

  .close-button {
    font-size: 22px;
    width: 22px;
    color: #515151;
    cursor: pointer;
  }
}

.modal-content {
  padding: 0 48px 48px 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 32px;

  p {
    margin: 0;
    padding: 0;
  }

  .container {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;

    &.welcome-elements {
      p {
        font-weight: 700;
        font-size: 24px;
        line-height: 28px;
        letter-spacing: -0.4px;
        color: #000000;
      }
    }

    &.funding-options {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: stretch;
      width: 100%;

      div {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: #257d72;
        padding: 8px 0;

        &:first-child {
          position: relative;
          padding-right: 24px;
        }

        &:last-child {
          padding-left: 24px;
        }

        .material-icons-outlined {
          font-size: 40px;
          width: 40px;
          height: 40px;
          margin-bottom: 8px;
        }

        p {
          font-weight: 700;
          font-size: 18px;
          line-height: 22px;
          letter-spacing: -0.4px;
          text-align: center;
          color: #257d72;
        }
      }
    }

    .title {
      font-weight: 700;
      font-size: 24px;
      line-height: 28px;
      letter-spacing: -0.4px;
      text-align: center;
      color: #515151;
      padding: 0 16px;
    }

    .subtitle {
      font-weight: 400;
      font-size: 20px;
      line-height: 24px;
      letter-spacing: -0.4px;
      text-align: center;
      color: #515151;
    }
  }

  .book-call-button {
    display: flex;
    align-items: center;
    justify-content: center;

    width: 100%;
    padding: 16px;
    border-radius: 12px;

    font-weight: 700;
    font-size: 24px;
    line-height: 28px;
    letter-spacing: -0.4px;
    text-align: center;
    vertical-align: middle;

    .icon {
      font-size: 24px;
      margin-left: 8px;
      width: 24px;
      font-weight: 400;
    }
  }

  .text-link {
    font-weight: 700;
    font-size: 24px;
    line-height: 28px;
    letter-spacing: -0.4px;
    text-align: center;
    vertical-align: middle;
    color: #1d76ac;
    text-decoration: none;

    &:hover {
      text-decoration: none;
    }
  }
}
</style>
